/**
 * Ayrshire Hub — shared UI primitives (web).
 *
 * These replace patterns that were previously copy-pasted inline:
 *  - Chip            → 120+ inline chip implementations across pages
 *  - StatusBadge     → 3 open/closed/paused variants
 *  - OfferBanner     → 2 divergent gradient definitions
 *  - SectionHeader   → defined in Home.tsx, redefined in AdminWallboard
 *  - Skeletons       → 8 near-identical *Skeleton() functions
 *
 * Pages should import from "@/components/hub" and never hand-roll these.
 */
import type { ReactNode } from "react";
import { Link } from "wouter";
import { AlertTriangle, ArrowRight, PauseCircle } from "lucide-react";
import { cn } from "@/lib/utils";
import { TONE_OUTLINE, TONE_OVERLAY, TONE_SOLID, type Tone } from "./tokens";

/* ── Chip ─────────────────────────────────────────────────────────────
   The single most duplicated pattern in the web app.                   */
export function Chip({
  children, icon, tone = "neutral", variant = "solid", size = "sm", className,
}: {
  children: ReactNode;
  icon?: ReactNode;
  tone?: Tone;
  /** solid = tinted fill; outline = bordered; overlay = dark, for use on imagery */
  variant?: "solid" | "outline" | "overlay";
  size?: "xs" | "sm" | "md";
  className?: string;
}) {
  const sizing = {
    xs: "text-[10px] px-2 py-0.5 gap-1",
    sm: "text-[11px] px-2.5 py-0.5 gap-1",
    md: "text-xs px-3 py-1 gap-1.5",
  }[size];

  const toneCls =
    variant === "overlay" ? TONE_OVERLAY
    : variant === "outline" ? cn("border bg-transparent", TONE_OUTLINE[tone])
    : TONE_SOLID[tone];

  return (
    <span className={cn(
      "inline-flex items-center rounded-full font-semibold whitespace-nowrap",
      sizing, toneCls, className,
    )}>
      {icon}
      {children}
    </span>
  );
}

/* ── StatusBadge ──────────────────────────────────────────────────────
   Open / closed / paused. Replaces 3 divergent inline treatments.      */
export function StatusBadge({
  open, label, className,
}: { open: boolean; label?: string; className?: string }) {
  if (open) {
    return (
      <Chip tone="success" size="xs" className={className}
        icon={<span className="w-1.5 h-1.5 rounded-full bg-emerald-500" />}>
        Open
      </Chip>
    );
  }
  return (
    <Chip variant="overlay" size="xs" className={className}
      icon={<PauseCircle size={11} />}>
      {label ?? "Closed"}
    </Chip>
  );
}

/** Centred "CLOSED" scrim for use over a store image. */
export function ClosedOverlay({ label }: { label?: string }) {
  return (
    <div className="absolute inset-0 bg-black/45 flex items-center justify-center">
      <div className="bg-white/95 rounded-lg px-3 py-1.5 flex items-center gap-1.5 shadow-sm">
        <PauseCircle size={13} className="text-slate-600" />
        <span className="text-xs font-bold text-slate-700">{label ?? "Closed"}</span>
      </div>
    </div>
  );
}

/* ── OfferBanner ──────────────────────────────────────────────────────
   One canonical gradient. Home and OrderStores previously disagreed.   */
export function OfferBanner({ children, compact = false }: { children: ReactNode; compact?: boolean }) {
  return (
    <div className={cn("flex items-center gap-2 bg-[#fff8e6] border-amber-200", compact ? "w-fit max-w-full px-2.5 py-1 rounded-full border" : "px-3 py-1.5 justify-center border-b")}>
      <span className="text-[#1e3a5f] text-[9px] font-semibold tracking-wide uppercase bg-amber-100 rounded px-1.5 py-0.5">
        DEAL
      </span>
      <span className="text-[#1e3a5f] text-[11px] font-semibold tracking-wide truncate">
        {children}
      </span>
    </div>
  );
}

/* ── SectionHeader ────────────────────────────────────────────────────
   Extracted from Home.tsx so every rail is aligned identically.        */
export function SectionHeader({
  title, subtitle, icon, href, seeAllLabel = "See all", action,
}: {
  title: string;
  subtitle?: string;
  icon?: ReactNode;
  href?: string;
  seeAllLabel?: string;
  action?: ReactNode;
}) {
  return (
    <div className="flex items-end justify-between gap-4 mb-3">
      <div className="min-w-0">
        <div className="flex items-center gap-2">
          {icon && <span className="text-[#1e3a5f] shrink-0">{icon}</span>}
          <h2 className="text-lg sm:text-xl font-bold tracking-tight text-slate-900 truncate">
            {title}
          </h2>
        </div>
        {subtitle && (
          <p className="text-[13px] text-slate-500 mt-0.5 truncate">{subtitle}</p>
        )}
      </div>
      {action ?? (href && (
        <Link
          href={href}
          className="shrink-0 inline-flex items-center gap-1 text-[13px] font-semibold text-[#1e3a5f] hover:gap-2 transition-all"
        >
          {seeAllLabel}
          <ArrowRight size={14} />
        </Link>
      ))}
    </div>
  );
}

/* ── Skeletons ────────────────────────────────────────────────────────
   Replaces EventsSkeleton / DealsSkeleton / NewsSkeleton /
   BusinessesSkeleton / FreeItemsSkeleton — all previously identical.   */
export function SkeletonBlock({ className }: { className?: string }) {
  return <div className={cn("skeleton-shimmer rounded-lg bg-slate-200/70", className)} />;
}

/** One media card placeholder: image block + two text lines. */
export function SkeletonCard({ imageClass = "h-40" }: { imageClass?: string }) {
  return (
    <div className="bg-white rounded-2xl border border-slate-100 overflow-hidden">
      <SkeletonBlock className={cn("rounded-none w-full", imageClass)} />
      <div className="p-3 space-y-2">
        <SkeletonBlock className="h-3.5 w-3/4" />
        <SkeletonBlock className="h-3 w-1/2" />
      </div>
    </div>
  );
}

/** Responsive grid of card placeholders. */
export function SkeletonGrid({
  count = 6, cols = 3, imageClass = "h-40",
}: { count?: number; cols?: 2 | 3 | 4; imageClass?: string }) {
  const colClass = { 2: "sm:grid-cols-2", 3: "sm:grid-cols-2 lg:grid-cols-3", 4: "sm:grid-cols-2 lg:grid-cols-4" }[cols];
  return (
    <div className={cn("grid grid-cols-1 gap-4", colClass)}>
      {Array.from({ length: count }).map((_, i) => (
        <SkeletonCard key={i} imageClass={imageClass} />
      ))}
    </div>
  );
}

/** Horizontal rail of card placeholders, matching ScrollRow metrics. */
export function SkeletonRail({
  count = 5, cardClass = "w-[220px]", imageClass = "h-32",
}: { count?: number; cardClass?: string; imageClass?: string }) {
  return (
    <div className="flex gap-3 overflow-hidden">
      {Array.from({ length: count }).map((_, i) => (
        <div key={i} className={cn("shrink-0", cardClass)}>
          <SkeletonCard imageClass={imageClass} />
        </div>
      ))}
    </div>
  );
}

/* ── InlineAlert ──────────────────────────────────────────────────────
   Replaces ad-hoc coloured notice boxes.                               */
export function InlineAlert({
  tone = "warning", title, children, action,
}: { tone?: Tone; title?: string; children?: ReactNode; action?: ReactNode }) {
  return (
    <div className={cn("rounded-xl border px-4 py-3 flex items-start gap-3", TONE_OUTLINE[tone], TONE_SOLID[tone])}>
      <AlertTriangle size={16} className="mt-0.5 shrink-0" />
      <div className="min-w-0 flex-1 text-sm">
        {title && <p className="font-bold">{title}</p>}
        {children && <div className={cn(title && "mt-0.5", "opacity-90")}>{children}</div>}
      </div>
      {action}
    </div>
  );
}

/* ── DeliveryCodeCard ─────────────────────────────────────────────────
   The 3-digit code the customer reads out to the hub driver at the door.
   The driver cannot complete the delivery without it, so this is
   deliberately the loudest element wherever it appears.

   Navy/amber is fixed rather than tone-driven: it is the brand's highest
   contrast pair and reads identically in light and dark mode. Kept in
   lockstep with the mobile DeliveryCodeCard (LAW 6 — parity).           */
export function DeliveryCodeCard({
  code, enRoute = false, size = "md", className,
}: {
  code: string;
  /** `true` once the driver has set off — raises emphasis and copy. */
  enRoute?: boolean;
  size?: "md" | "lg";
  className?: string;
}) {
  const caption = enRoute
    ? "Give this code to your driver at the door."
    : "Give this code to your driver when they arrive.";
  return (
    <div
      className={cn(
        "flex flex-col items-center rounded-xl bg-[#1e3a5f] px-4",
        size === "lg" ? "py-5" : "py-3",
        enRoute && "ring-2 ring-[#f0a500]",
        className,
      )}
      // Digits are spelled out for screen readers — otherwise "482" is read as
      // "four hundred and eighty-two", which is not what the customer needs to
      // say to the driver.
      role="group"
      aria-label={`Your delivery code is ${code.split("").join(" ")}. ${caption}`}
    >
      <p className="text-[10px] font-bold uppercase tracking-widest text-white/70 mb-1">
        {enRoute ? "Driver on the way · Your code" : "Your delivery code"}
      </p>
      <p
        aria-hidden="true"
        className={cn(
          "font-bold text-[#f0a500] tracking-[0.3em] ml-[0.3em] leading-none",
          size === "lg" ? "text-5xl" : "text-3xl",
        )}
      >
        {code}
      </p>
      <p className="text-[11px] text-white/60 mt-1.5 text-center">{caption}</p>
    </div>
  );
}

/* ── Divider ──────────────────────────────────────────────────────── */
export function Divider({ className }: { className?: string }) {
  return <hr className={cn("border-slate-100", className)} />;
}
